import { getAdpApiHeaders } from './adpDataSources.api'
import { config } from '@/config'

// ─── Types ────────────────────────────────────────────────────────────────────

export type DynamicFieldOption = {
    label: string
    value: string
}

export type DynamicField = {
    type: 'input' | 'select' | 'multiselect' | 'textarea' | 'group' | 'code_editor'
    label: string
    key: string
    required?: boolean
    placeholder?: string
    dataSource?: string
    editable?: boolean
    options?: DynamicFieldOption[]
    repeatable?: boolean
    multiple?: boolean
    fields?: DynamicField[]
    addButtonLabel?: string
    allowRemove?: boolean
}

export type DynamicSchemaDefinition = {
    id: string
    label: string
    type: string
    apiKey: string
    version: string
    fields?: DynamicField[]
}

export type IntegrationTypeDefinition = {
    id: number
    name: string
    description: string
    abbreviation: string
    min_no_of_columns: number
    schema_definition?: DynamicSchemaDefinition | null
}

type IntegrationsApiResponse = {
    success: boolean
    status: number
    message?: string
    data?: IntegrationTypeDefinition[]
}

// ─── API function ─────────────────────────────────────────────────────────────

async function fetchIntegrationsUrl(
    url: string
): Promise<IntegrationTypeDefinition[] | null> {
    const res = await fetch(url, {
        method: 'GET',
        headers: getAdpApiHeaders(),
    })

    if (!res.ok) {
        if (res.status === 404) {
            return null
        }
        const json = (await res.json().catch(() => ({}))) as IntegrationsApiResponse
        throw new Error(json.message || `Integrations request failed (${res.status})`)
    }

    const json = (await res.json().catch(() => ({}))) as IntegrationsApiResponse
    if (!json.success || !Array.isArray(json.data)) {
        throw new Error(json.message || 'Invalid integrations response')
    }

    // Filter to only include join and union
    return json.data.filter(item =>
        item.name.toLowerCase() === 'join' || item.name.toLowerCase() === 'union'
    )
}

export async function fetchIntegrations(
    baseUrl = config.adpApiUrl
): Promise<IntegrationTypeDefinition[]> {
    const normalizedBase = (typeof baseUrl === 'string' ? baseUrl.trim() : String(baseUrl)).replace(/\/+$/, '')
    if (!normalizedBase) {
        throw new Error('ADP API URL is not configured')
    }

    const candidates = [`${normalizedBase}/transformations`, `${normalizedBase}/records/transform_type`]

    let lastError: Error | null = null

    for (const url of candidates) {
        const result = await fetchIntegrationsUrl(url)
        if (result !== null) {
            return result
        }
        lastError = new Error(`Integrations endpoint not found: ${url}`)
    }

    throw lastError ?? new Error('Integrations endpoint not found')
}
